import React from 'react';
import { StageSection, Prose, KeyPoints, NumberedFlow } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'dApps & Oracles',
    tagline: 'How a web page talks to a contract, and why contracts can\'t see the outside world.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - what a dapp frontend actually does vs. what lives on-chain
//   Q1 - why contracts need oracles at all (the "sealed box" problem)
export const blocks: StageBlock[] = [
    content('dapp-anatomy', () => (
        <StageSection gradientBorder eyebrow="Stage 8 of 8" title="A dapp is mostly a normal web app with one extra dependency">
            <Prose>
                A decentralized application is typically an ordinary frontend (HTML, JS, a framework like this
                platform's own React app) plus a wallet connection. The frontend reads chain state and builds
                transactions; it never holds funds or private keys itself. The actual logic and the actual state
                that matters live entirely in the deployed contract.
            </Prose>
            <NumberedFlow steps={[
                ['Frontend reads state', 'It calls view functions over JSON-RPC to display balances, prices, or contract data: no transaction, no gas, no signature required.'],
                ['User initiates an action', 'The frontend encodes a function call and its arguments, and asks the connected wallet to sign it.'],
                ['Wallet signs and broadcasts', 'The signed transaction goes to the network; the frontend never sees the private key.'],
                ['Frontend reflects the new state', 'Once the transaction is mined, the frontend re-reads chain state to show the result.'],
            ]} />
        </StageSection>
    )),

    content('trust-model', () => (
        <StageSection title="A malicious frontend can still lie to you">
            <Prose>
                Because the frontend is just a convenience layer, a compromised or malicious one can display false
                information, encode a different transaction than what it shows you, or point at the wrong contract
                entirely. The contract itself is the actual source of truth, which is why reading the bytecode or
                verified source directly (rather than trusting whatever a website renders) matters when real value
                is involved.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('oracle-problem', () => (
        <StageSection title="The oracle problem: contracts can't see outside their own chain">
            <Prose>
                The EVM can only read data that's already inside Ethereum's own state; it has no built-in way to
                fetch a stock price, a sports score, or even reliable off-chain randomness. Every value that
                originates outside the chain has to be pushed <em>into</em> a transaction by something willing to
                submit it. That something is an <strong>oracle</strong>.
            </Prose>
            <KeyPoints items={[
                <>An oracle is ultimately a trust decision: a contract that depends on off-chain data is only as reliable as whatever reports that data on-chain.</>,
                <>A single, centralized oracle reintroduces exactly the kind of trusted middleman Ethereum otherwise avoids, which is why designs like decentralized oracle networks, aggregating many independent reporters, exist.</>,
                <>Confusing an easily-manipulated on-chain value (like a single DEX pool's spot price) for a trustworthy oracle is a recurring real-world exploit pattern, and one you'll meet again in later challenge categories.</>,
            ]} />
        </StageSection>
    )),

    question(1),

    content('closing', () => (
        <StageSection title="From theory to exploitation">
            <Prose>
                You now have the pieces this whole platform assumes you already know: accounts and keys, how a
                transaction becomes a metered state change, what the EVM actually executes, how validators agree on
                one chain, and where contracts get their outside information from. The Introduction and Solidity
                tracks build directly on this, and every security category from here on is really just a specific
                way one of these mechanisms gets misused.
            </Prose>
        </StageSection>
    )),
];
